import type { MeshData } from '@manudev/model';
import type { PropsElement, Storey } from '@manudev/assistant';
import { elementPreset, partPreset, presetByType, type PresetId } from '@manudev/pipeline/presets';
import * as THREE from 'three';

// What GlbLayer and StoreLayer both hand to buildBatches: a tree shaped like the pipeline's
// GLBs (storey group > element group > mesh), whatever it was built from.
export interface MeshSource {
  object: THREE.Object3D;
}

// Meshes carry their material preset in userData.preset. The pipeline writes it; GLBs converted
// before it did get their element's preset from the IFC type alone (names like "Roof Drain" on
// MEP parts would mislead the name rules).
export function glbSource(scene: THREE.Object3D): MeshSource {
  const object = scene.clone(true);
  object.traverse((el) => {
    const x = el.userData as { kind?: string; type?: string; name?: string; preset?: string };
    if (x.kind !== 'element') return;
    const preset = x.preset ?? presetByType(x.type ?? '');
    el.traverse((m) => {
      if ((m as THREE.Mesh).isMesh) m.userData.preset ??= preset;
    });
  });
  return { object };
}

// What meshSource needs of a mesh: the fields @manudev/model and @manudev/rvt MeshData share.
export interface SourceMesh {
  expressId: number;
  ifcType?: string;
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
  color: [number, number, number, number];
  materialName?: string;
  materialId?: number;
  origin?: [number, number, number];
}

// The least an element has to say about itself to be grouped, named and given a preset.
export interface SourceElement {
  expressID: number;
  type: string;
  name: string;
  globalId?: string;
  storeyId: number | null;
  materials?: string[];
  // wins over the name rules, for sources that know better than the IFC names
  preset?: PresetId;
}

export interface SourceStorey {
  expressID: number;
  name: string;
  elevation: number;
}

// Groups meshes into the same storey > element > mesh shape buildBatches expects, using the
// element list for names and storeys since a mesh itself only carries id, type, colour and
// material name.
export function meshSource(
  meshes: SourceMesh[],
  elements: SourceElement[],
  storeys: SourceStorey[],
): MeshSource {
  const root = new THREE.Group();
  const byId = new Map(elements.map((e) => [e.expressID, e]));
  const byExpress = new Map<number, SourceMesh[]>();
  for (const m of meshes) {
    const list = byExpress.get(m.expressId);
    if (list) list.push(m);
    else byExpress.set(m.expressId, [m]);
  }

  const storeyGroups = new Map<number | null, THREE.Group>();
  const storeyGroup = (storeyId: number | null): THREE.Group => {
    const existing = storeyGroups.get(storeyId);
    if (existing) return existing;
    const storey = storeys.find((s) => s.expressID === storeyId);
    const group = new THREE.Group();
    group.name = storey?.name ?? 'Unplaced';
    group.userData = { kind: 'storey', elevation: storey?.elevation ?? 0 };
    storeyGroups.set(storeyId, group);
    root.add(group);
    return group;
  };

  for (const [expressID, parts] of byExpress) {
    const el = byId.get(expressID);
    const type = el?.type ?? parts[0]?.ifcType ?? '';
    const names = el?.materials ?? parts.flatMap((p) => p.materialName ?? []);
    const preset = el?.preset ?? elementPreset(names, el?.name ?? '', type);
    const group = new THREE.Group();
    group.name = el?.name ?? `#${expressID}`;
    group.userData = {
      kind: 'element',
      expressID,
      globalId: el?.globalId ?? '',
      type,
      name: el?.name ?? '',
      preset,
    };
    for (const part of parts) {
      const mesh = meshFrom(part);
      const layer = part.materialId != null ? part.materialName : undefined;
      mesh.userData.preset = partPreset(layer, part.color[3], preset, type);
      group.add(mesh);
    }
    storeyGroup(el?.storeyId ?? null).add(group);
  }

  return { object: root };
}

// The store's props and storeys as meshSource's element and storey lists.
export function storeSource(
  meshes: MeshData[],
  elements: PropsElement[],
  storeys: Storey[],
): MeshSource {
  return meshSource(
    meshes,
    elements.map((e) => ({
      expressID: e.expressID,
      type: e.type,
      name: e.name,
      globalId: e.globalId,
      storeyId: e.storeyId,
      materials: e.material?.names,
    })),
    storeys.map((s) => ({ expressID: s.expressID, name: s.name, elevation: s.elevation ?? 0 })),
  );
}

function meshFrom(data: SourceMesh): THREE.Mesh {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
  geometry.setIndex(new THREE.BufferAttribute(data.indices, 1));
  const [r, g, b, a] = data.color;
  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color(r, g, b),
    transparent: a < 1,
    opacity: a,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  // ifc-lite keeps element-local positions and a world origin per element, for f32 precision
  if (data.origin) mesh.position.set(data.origin[0], data.origin[1], data.origin[2]);
  return mesh;
}
